import type { InfiniteData } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { CommunityFeedItemV2DtoPaginationResultDto, PollDto } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useDeletePoll = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ pollId }: { pollId: string }) => api.deletePollsByIdV1(pollId),
    onSuccess: async () => {
      showFlashToast({
        type: "success",
        title: t("component.community-poll.delete.success"),
      });
      await queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.MESSAGES(projectId), "FEED"],
      });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("component.community-poll.delete.error"),
      });
    },
  });
};

const useAddPollVotes = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ pollId, data }: { pollId: string; data: { votes: string[] } }) =>
      api.postPollsVoteV1(pollId, data.votes),
    onSuccess: async (_, payload) => {
      // Fetch latest poll answers
      const newAnswers = await api.getPollsAnswersV1(payload.pollId).then(commonAPIDataSelector);

      // Optimistically update the poll results in the feed
      queryClient.setQueriesData<InfiniteData<CommunityFeedItemV2DtoPaginationResultDto>>(
        {
          queryKey: QUERY_KEYS.MESSAGES_FEED(projectId),
        },
        (currData) => {
          if (!currData) return currData;

          const updatedPages = currData.pages.map((page) => {
            const updatedItems = page.items.map((item) => {
              if (item.type !== "poll" || item.poll?.id !== payload.pollId) return item;

              return {
                ...item,
                poll: {
                  ...item.poll,
                  results: newAnswers.pollResults,
                  canRespond: "alreadyAnswered",
                } satisfies PollDto,
              };
            });

            return {
              ...page,
              items: updatedItems,
            };
          });

          return {
            ...currData,
            pages: updatedPages,
          };
        },
      );

      showFlashToast({
        type: "success",
        title: t("component.community-poll.vote.success"),
      });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("component.community-poll.vote.error"),
      });
    },
  });
};

const useUpdatePollReadStatus = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ pollId }: { pollId: string }) => api.postPollsReadV1(pollId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...QUERY_KEYS.MESSAGES(projectId), "FEED"],
      });
      await queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.MESSAGES_FEED_STATS(projectId),
      });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("component.community-poll.mark-as-read.error"),
      });
    },
  });
};

const useDeletePollVotes = () => {
  const api = useApi();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sessionUser = useSessionUser();
  const projectId = useProjectId();

  return useMutation({
    mutationFn: ({ pollId }: { pollId: string }) => api.deletePollsVoteV1(pollId),
    onSuccess: async (_, payload) => {
      // Fetch latest poll answers
      const newAnswers = await api.getPollsAnswersV1(payload.pollId).then(commonAPIDataSelector);

      // Optimistically update the poll results in the feed
      queryClient.setQueriesData<InfiniteData<CommunityFeedItemV2DtoPaginationResultDto>>(
        {
          queryKey: QUERY_KEYS.MESSAGES_FEED(projectId),
        },
        (currData) => {
          if (!currData) return currData;

          const updatedPages = currData.pages.map((page) => {
            const updatedItems = page.items.map((item) => {
              if (item.type !== "poll" || item.poll?.id !== payload.pollId) return item;

              const canSeeResults =
                sessionUser.isSuperAdmin ||
                item.poll.createdBy.id === sessionUser.id ||
                Boolean(item.poll.endsAt && new Date() > new Date(item.poll.endsAt));

              return {
                ...item,
                poll: {
                  ...item.poll,
                  results: newAnswers.pollResults,
                  canRespond: "yes",
                  canSeeResults,
                } satisfies PollDto,
              };
            });

            return {
              ...page,
              items: updatedItems,
            };
          });

          return {
            ...currData,
            pages: updatedPages,
          };
        },
      );

      showFlashToast({
        type: "success",
        title: t("component.community-poll.clear-vote.success"),
      });
    },
    onError: () => {
      showFlashToast({
        type: "error",
        title: t("component.community-poll.clear-vote.error"),
      });
    },
  });
};

export const pollMutations = {
  useDeletePoll,
  useUpdatePollReadStatus,
  useAddPollVotes,
  useDeletePollVotes,
};
